import { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Zap } from 'lucide-react';
import classnames from 'classnames';
import { OTP_DELIVERY_CHANNELS } from '../../services/exports/Constants';
import OtpCodeInput from './OtpCodeInput';
import WhatsAppSenderNote from './WhatsAppSenderNote';
import { resolveOtpDestination } from '../../utils/otp';

interface Props {
  code: string;
  onCodeChange: (value: string) => void;
  phoneNumber: string;
  // Where the code actually went, as returned by the API — the phone number on the phone
  // channels, a masked mailbox on email. Falls back to the phone number when absent.
  deliveryAddress?: string;
  // The channel the code was sent on; drives the subtitle, resend label and the switch option.
  channel: OTP_DELIVERY_CHANNELS;
  // Called for both the resend (same channel) and switch (other channel) actions.
  onResend: (channel: OTP_DELIVERY_CHANNELS) => void;
  // Seconds left in the resend lockout. While > 0 the countdown shows and resend is disabled.
  retryAfter?: number;
  resending?: boolean;
  // Captcha widget, rendered once the lockout elapses so a fresh single-use token can be solved.
  turnstile?: ReactNode;
  shouldAutoFocus?: boolean;
  numInputs?: number;
  className?: string;
  // Extra content rendered below the resend options (e.g. the verify/submit button).
  children?: ReactNode;
}

// Shared "enter the verification code" step. Rendered identically by the web OtpFlow and the
// mobile login OtpForm so both surfaces look the same — header, the digit boxes, the WhatsApp
// sender note and the resend/switch options.
export default function OtpVerificationStep(props: Props) {
  const {
    code,
    onCodeChange,
    phoneNumber,
    deliveryAddress,
    channel,
    onResend,
    retryAfter = 0,
    resending,
    turnstile,
    shouldAutoFocus,
    numInputs = 4,
    className,
    children,
  } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Auth:OtpFlow' });

  const isLocked = retryAfter > 0;
  const otherChannel =
    channel === OTP_DELIVERY_CHANNELS.WHATSAPP ? OTP_DELIVERY_CHANNELS.SMS : OTP_DELIVERY_CHANNELS.WHATSAPP;
  const sentTo = resolveOtpDestination(channel, phoneNumber, deliveryAddress);
  const minutes = String(Math.floor(retryAfter / 60)).padStart(2, '0');
  const seconds = String(retryAfter % 60).padStart(2, '0');

  return (
    <div className={classnames('space-y-md', className)}>
      {/* Header */}
      <div className="text-center">
        <div className={otpHeaderIcon}>
          <Zap className="w-5 h-5 text-brand-primary" />
        </div>
        <h3 className="text-md font-bold text-brand-text-primary mb-2xs">{t('otp.title')}</h3>
        <p className="text-sm text-muted-foreground mp-mask">
          <Trans
            t={t}
            i18nKey={`otp.sent_via.${channel}`}
            values={{ phone: sentTo }}
            components={{ strong: <span className="text-brand-text-primary font-medium" /> }}
          />
        </p>
      </div>

      {/* OTP input */}
      <OtpCodeInput value={code} onChange={onCodeChange} numInputs={numInputs} shouldAutoFocus={shouldAutoFocus} />

      {/* "Check your inbox and your spam folder." The copy existed in both languages but
          was rendered nowhere, and with no SMS fallback it is the only mitigation a
          customer whose code landed in spam gets. Empty for the phone channels. */}
      {Boolean(t(`otp.tip.${channel}`)) && (
        <p className="text-center text-xs text-muted-foreground" data-test="otp-tip">
          {t(`otp.tip.${channel}`)}
        </p>
      )}

      {channel === OTP_DELIVERY_CHANNELS.WHATSAPP && <WhatsAppSenderNote />}

      {/* Resend options */}
      <div className="space-y-xs">
        <p className="text-center text-xs text-muted-foreground">
          <span>{t('otp.resend.title')}</span>
          {isLocked && (
            <span>
              {' '}
              {t('otp.resend.countdown')}{' '}
              <span className="font-semibold text-brand-text-primary">
                {minutes}:{seconds}
              </span>
            </span>
          )}
        </p>

        {/* Once the resend timer elapses, show the captcha so the user can solve a fresh
            single-use token before resending. */}
        {!isLocked && turnstile}

        <button
          type="button"
          onClick={() => onResend(channel)}
          disabled={isLocked || resending}
          className={resendActionButton}
          data-test={`otp-resend-${channel}`}
        >
          <span>{t(`otp.resend.options.${channel}.button`)}</span>
        </button>

        {channel !== OTP_DELIVERY_CHANNELS.EMAIL && (
          <button
            type="button"
            onClick={() => onResend(otherChannel)}
            disabled={isLocked || resending}
            className={resendActionButton}
            data-test={`otp-switch-${otherChannel}`}
          >
            <span>{t(`otp.resend.switch.${otherChannel}`)}</span>
          </button>
        )}
      </div>

      {children}
    </div>
  );
}

// Styles
const otpHeaderIcon = 'w-12 h-12 bg-brand-primary/10 rounded-full flex items-center justify-center mx-auto mb-sm';
const resendActionButton =
  'w-full flex items-center justify-center gap-xs rounded-full border-[1px] border-brand-border bg-card px-sm py-sm text-sm font-medium text-brand-text-primary transition-colors hover:border-brand-primary/40 disabled:cursor-not-allowed disabled:opacity-60';
